import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  ApiError,
  acknowledgeAllPages,
  complete,
  decline,
  exchange,
  fetchDocument,
  loadEnvelope,
  type Session,
  type SessionEnvelope,
} from './api.ts';
import { INTENT_STATEMENT, INTENT_STATEMENT_VERSION, SIGN_BUTTON_LABEL } from './intent.ts';

/**
 * The faces a signature may be adopted in, and what each renders as here.
 *
 * The identifier goes into the digest (spec/04 §5), so it names a rendering
 * rather than a font file — and changing what one of these renders as is a new
 * identifier, not an edit to this list.
 *
 * They name the standard 14 because the signed document can only show a face
 * it embeds, and nothing embeds a font yet. A `cursive` identifier would
 * render as script here and as Helvetica in the PDF: one name, two
 * appearances, which is exactly what the identifier exists to prevent.
 *
 * `pnpm typefaces` fails the build if this list and the server's disagree.
 */
const TYPEFACES = [
  {
    id: 'standard-helvetica-v1' as const,
    name: 'Sans',
    css: 'Helvetica, Arial, sans-serif',
  },
  {
    id: 'standard-times-italic-v1' as const,
    name: 'Italic serif',
    css: "'Times New Roman', Times, serif",
    italic: true,
  },
];

type Typeface = (typeof TYPEFACES)[number]['id'];

type Phase = 'loading' | 'access-code' | 'ready' | 'signed' | 'declined' | 'error';

export function SigningPage({ token }: { token: string }): ReactElement {
  const [phase, setPhase] = useState<Phase>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const [envelope, setEnvelope] = useState<SessionEnvelope | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [presentedHash, setPresentedHash] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [accessCode, setAccessCode] = useState('');
  /**
   * What the signer typed, by field id.
   *
   * Before this, every non-signature field was filled with the party's name
   * and submitted as though they had entered it — a value attributed to
   * somebody who never typed it, in a record whose whole purpose is evidence
   * (spec/06). A field the signer fills is a field the signer fills.
   */
  const [entries, setEntries] = useState<Record<string, string>>({});
  /**
   * The name the signer types to adopt as their signature.
   *
   * Deliberately not prefilled with the name on the envelope. A form with a
   * value in it that gets submitted is not somebody adopting a signature, and
   * recording it as one would put a false fact in the record (spec/04 §5).
   */
  const [adopted, setAdopted] = useState('');
  const [typeface, setTypeface] = useState<Typeface>('standard-helvetica-v1');
  const objectUrl = useRef<string | null>(null);

  // Revoked on unmount. An object URL held after the view is gone keeps the
  // document in memory for the life of the tab.
  useEffect(
    () => () => {
      if (objectUrl.current !== null) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );

  const start = useCallback(
    async (code?: string) => {
      setProblem(null);
      setPhase('loading');
      try {
        const opened = await exchange(token, code);
        setSession(opened);
        setPresentedHash(opened.hashPresented);
        setEnvelope(await loadEnvelope(opened.sessionToken));

        const { blob, itemSha256 } = await fetchDocument(opened.sessionToken);
        const url = URL.createObjectURL(blob);
        objectUrl.current = url;
        setDocumentUrl(url);
        // itemSha256 covers the bytes just fetched; the presented hash covers
        // every item this party was shown (spec/05 §4.4). Only the second is
        // echoed at complete, so the exchange's value stands — overwriting it
        // with an item hash works on a one-document envelope and fails on the
        // first two-document one, with a mismatch that looks like tampering.
        void itemSha256;

        setPhase('ready');
      } catch (error) {
        if (error instanceof ApiError && error.code === 'ACCESS_CODE_REQUIRED') {
          setPhase('access-code');
          return;
        }
        if (error instanceof ApiError && error.code === 'ACCESS_CODE_INVALID') {
          setPhase('access-code');
          setProblem('That access code is not right.');
          return;
        }
        setProblem(error instanceof Error ? error.message : String(error));
        setPhase('error');
      }
    },
    [token],
  );

  useEffect(() => {
    void start();
  }, [start]);

  async function markReviewed(): Promise<void> {
    if (session === null || envelope === null) return;
    const item = envelope.items[0];
    if (item === undefined) return;
    setBusy(true);
    try {
      // Recorded as the explicit acknowledgement R-03 permits, and the server
      // stores it as that rather than as pages turned. Page-level tracking
      // needs a viewer that reports scroll position; until there is one, this
      // says what actually happened.
      await acknowledgeAllPages(session.sessionToken, item.id);
      setReviewed(true);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function sign(): Promise<void> {
    if (session === null || envelope === null || presentedHash === null) return;
    setBusy(true);
    setProblem(null);
    try {
      const values = envelope.fields
        .filter((field) => !field.readOnly)
        .map((field) => ({
          fieldId: field.id,
          value: isSignature(field.type)
            ? {
                kind: 'signature',
                capture: 'TYPE',
                // What was adopted, and the face it was shown in. No hash: the
                // server computes that over a published canonical form, so the
                // recorded digest is one it derived rather than one this page
                // asserted (spec/04 §5).
                adopted: { text: adopted.trim(), typeface },
              }
            : { kind: 'text', text: entries[field.id] ?? '' },
        }));

      await complete(session.sessionToken, {
        values,
        presentedHash,
        intentStatementVersion: INTENT_STATEMENT_VERSION,
        intentAcknowledged: true,
      });
      setPhase('signed');
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  async function refuse(): Promise<void> {
    if (session === null) return;
    const reason = window.prompt('If you would like to say why, you can. It is not required.');
    if (reason === null) return;
    setBusy(true);
    try {
      await decline(session.sessionToken, reason.trim() === '' ? undefined : reason.trim());
      setPhase('declined');
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  if (phase === 'loading') return <Centre>Loading your document…</Centre>;

  if (phase === 'access-code') {
    return (
      <Centre>
        <h2>This link needs an access code</h2>
        <p className="muted">The sender will have given it to you separately.</p>
        {problem !== null && <p className="error">{problem}</p>}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void start(accessCode);
          }}
        >
          <input
            value={accessCode}
            onChange={(event) => setAccessCode(event.target.value)}
            aria-label="Access code"
            autoComplete="one-time-code"
          />{' '}
          <button className="sign" type="submit">Continue</button>
        </form>
      </Centre>
    );
  }

  if (phase === 'error') {
    return (
      <Centre>
        <h2>This link cannot be opened</h2>
        <p className="error">{problem}</p>
      </Centre>
    );
  }

  if (phase === 'signed') {
    return (
      <Centre>
        <h2>Signed</h2>
        <p>Your signature has been recorded.</p>
        {documentUrl !== null && (
          <p>
            <a className="download" href={documentUrl} download>
              Download your copy
            </a>
          </p>
        )}
      </Centre>
    );
  }

  if (phase === 'declined') {
    return (
      <Centre>
        <h2>Declined</h2>
        <p>You have declined to sign. Everyone on this envelope has been told it stopped.</p>
      </Centre>
    );
  }

  const canSign = envelope?.canSign === true;

  /** Fields this signer fills in themselves — everything that is not a signature. */
  const entryFields = (envelope?.fields ?? []).filter(
    (field) => !isSignature(field.type) && !field.readOnly,
  );
  const missing = entryFields.filter(
    (field) => field.required && (entries[field.id] ?? '').trim() === '',
  );

  /** Whether any of this signer's fields is a signature at all. */
  const needsSignature = (envelope?.fields ?? []).some(
    (field) => isSignature(field.type) && !field.readOnly,
  );

  const ready =
    canSign &&
    reviewed &&
    acknowledged &&
    presentedHash !== null &&
    missing.length === 0 &&
    (!needsSignature || adopted.trim() !== '');

  return (
    <>
      <header className="bar">
        <h1>{envelope?.envelope.title}</h1>
        <p>
          Signing as {session?.party.name ?? session?.party.email ?? 'you'}
          {session?.party.email !== null && session?.party.name !== null
            ? ` · ${session?.party.email}`
            : ''}
        </p>
      </header>

      <main className="wrap">
        {!canSign && (
          <p className="notice">
            This envelope is not open for you to sign. You can still read and download it.
          </p>
        )}

        {/* NN-07: the document is shown in an ordinary viewer and offered for
            download. Nothing here disables printing, saving or copying, and
            there is no setting that would. */}
        {documentUrl !== null && (
          <>
            <iframe className="viewer" src={documentUrl} title="The document you are signing" />
            <div className="actions">
              <a className="download" href={documentUrl} download>
                Download or print this document
              </a>
              {!reviewed && canSign && (
                <button className="link" onClick={() => void markReviewed()} disabled={busy}>
                  I have reviewed all pages
                </button>
              )}
              {reviewed && <span className="muted">Reviewed ✓</span>}
            </div>
          </>
        )}

        {problem !== null && <p className="error">{problem}</p>}

        {canSign && (
          <section className="assent" aria-labelledby="assent-heading">
            <h2 id="assent-heading" style={{ marginTop: 0, fontSize: '1.05rem' }}>
              Sign this document
            </h2>

            {entryFields.length > 0 && (
              <div className="entries">
                {entryFields.map((field, index) => (
                  <p key={field.id}>
                    <label htmlFor={`entry-${field.id}`}>
                      {/*
                        Numbered rather than named: the API carries no label for
                        a field yet, and inventing one here would put words on
                        screen that the sender never wrote. The page number is
                        what actually helps somebody find it in the document.
                      */}
                      {field.type === 'TEXT' ? 'Text' : field.type} {index + 1} — page {field.page}
                      {field.required ? '' : ' (optional)'}
                    </label>
                    <input
                      id={`entry-${field.id}`}
                      type="text"
                      value={entries[field.id] ?? ''}
                      required={field.required}
                      onChange={(event) =>
                        setEntries((current) => ({ ...current, [field.id]: event.target.value }))
                      }
                    />
                  </p>
                ))}
              </div>
            )}

            {needsSignature && (
              <div className="adopt">
                <label htmlFor="adopted">Type your name to sign</label>
                <input
                  id="adopted"
                  type="text"
                  value={adopted}
                  onChange={(event) => setAdopted(event.target.value)}
                  autoComplete="off"
                  aria-describedby="adopt-help"
                  maxLength={200}
                />
                <p id="adopt-help" className="muted">
                  This is what will appear as your signature. It is recorded
                  exactly as you type it.
                </p>

                <fieldset className="faces">
                  <legend>How it looks</legend>
                  {TYPEFACES.map((face) => (
                    <label key={face.id} htmlFor={`face-${face.id}`}>
                      <input
                        id={`face-${face.id}`}
                        type="radio"
                        name="typeface"
                        checked={typeface === face.id}
                        onChange={() => setTypeface(face.id)}
                      />
                      {/* The preview is the whole point of the choice, so it is
                          the label — and it is empty until they type, because
                          showing a specimen name would be showing somebody
                          else's signature. */}
                      <span
                        className="specimen"
                        style={{
                          fontFamily: face.css,
                          fontStyle: face.italic === true ? 'italic' : 'normal',
                        }}
                      >
                        {adopted.trim() === '' ? face.name : adopted}
                      </span>
                    </label>
                  ))}
                </fieldset>
              </div>
            )}

            {/* R-02: immediately adjacent to the control, body-text sized, full
                contrast. Not a footer, not grey, not smaller. */}
            <p className="statement">{INTENT_STATEMENT}</p>

            <label>
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              <span>I have read and agree to the statement above.</span>
            </label>

            {!reviewed && (
              <p className="muted">
                Confirm you have reviewed all pages before signing.
              </p>
            )}

            {needsSignature && adopted.trim() === '' && (
              <p className="muted">Type your name above before signing.</p>
            )}

            {missing.length > 0 && (
              <p className="muted">
                {missing.length === 1
                  ? 'One field still needs filling in before you can sign.'
                  : `${missing.length} fields still need filling in before you can sign.`}
              </p>
            )}

            <div className="actions">
              {/* R-01: signing language, never Continue or Submit. */}
              <button className="sign" onClick={() => void sign()} disabled={!ready || busy}>
                {SIGN_BUTTON_LABEL}
              </button>
              <button className="link" onClick={() => void refuse()} disabled={busy}>
                Decline to sign
              </button>
            </div>

            {presentedHash !== null && (
              <p className="muted">
                Document fingerprint <span className="hash">{presentedHash}</span>
              </p>
            )}
          </section>
        )}
      </main>
    </>
  );
}

function isSignature(type: string): boolean {
  return type === 'SIGNATURE' || type === 'FREE_SIGNATURE' || type === 'INITIALS' || type === 'STAMP';
}

function Centre({ children }: { children: ReactNode }): ReactElement {
  return <main className="wrap">{children}</main>;
}
